/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';

function State() {
    const [states, setStates] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newRecord, setNewRecord] = useState({ name: '', country_id: '' });
    const [selectedState, setSelectedState] = useState(null);
    const formRef = useRef(null);

    // Table state
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // ─── Filter & Sort ───────────────────────────────────────────────────────────

    const filtered = states.filter(s =>
        s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.country?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = React.useMemo(() => {
        let sortable = [...filtered];
        if (sortConfig.key) {
            sortable.sort((a, b) => {
                let aVal = sortConfig.key === 'country' ? (a.country?.name ?? '') : (a[sortConfig.key] ?? '');
                let bVal = sortConfig.key === 'country' ? (b.country?.name ?? '') : (b[sortConfig.key] ?? '');
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortable;
    }, [filtered, sortConfig]);

    const totalItems = sorted.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = sorted.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
        return '';
    };

    useEffect(() => { setCurrentPage(1); }, [searchTerm]);

    // ─── API ─────────────────────────────────────────────────────────────────────

    const fetchStates = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/api/state');
            setStates(res.data.data || res.data || []);
        } catch {
            Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to fetch states' });
        } finally {
            setLoading(false);
        }
    };

    const fetchCountries = async () => {
        try {
            const res = await axiosInstance.get('/api/country');
            setCountries(res.data.data || res.data || []);
        } catch { }
    };

    useEffect(() => {
        fetchStates();
        fetchCountries();
    }, []);

    // ─── Handlers ────────────────────────────────────────────────────────────────

    const handleChange = (e) => {
        setNewRecord(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const resetForm = () => {
        setNewRecord({ name: '', country_id: '' });
        setSelectedState(null);
        if (formRef.current) formRef.current.classList.remove('was-validated');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formRef.current.checkValidity()) {
            formRef.current.classList.add('was-validated');
            return;
        }
        try {
            await axiosInstance.post('/api/state', newRecord);
            Swal.fire({ icon: 'success', title: 'Created!', text: 'State added successfully.', timer: 1500, showConfirmButton: false });
            window.$('#add_state').modal('hide');
            resetForm();
            fetchStates();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create state' });
        }
    };

    const handleEditClick = (state) => {
        setSelectedState(state);
        setNewRecord({ name: state.name || '', country_id: state.country_id || '' });
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedState) return;
        try {
            await axiosInstance.put(`/api/state/${selectedState.id}`, newRecord);
            Swal.fire({ icon: 'success', title: 'Updated!', text: 'State updated successfully.', timer: 1500, showConfirmButton: false });
            window.$('#edit_state').modal('hide');
            resetForm();
            fetchStates();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update state' });
        }
    };

    const handleDelete = async (state) => {
        const result = await Swal.fire({
            title: 'Delete State?',
            text: `Are you sure you want to delete "${state.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete!'
        });
        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/api/state/${state.id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
                fetchStates();
            } catch {
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete state' });
            }
        }
    };

    // ─── Pagination ───────────────────────────────────────────────────────────────

    const Pagination = () => {
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);
        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, totalItems)} of {totalItems} entries</div>
                <nav>
                    <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(1)}><i className="fa fa-angle-double-left"></i></button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                        </li>
                        {pageNumbers.map(n => (
                            <li key={n} className={`page-item ${n === currentPage ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(n)}>{n}</button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(totalPages)}><i className="fa fa-angle-double-right"></i></button>
                        </li>
                    </ul>
                </nav>
            </div>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Location Management</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <a className="btn btn-primary" href="#" data-bs-toggle="modal" data-bs-target="#add_state" onClick={resetForm}>
                                            <i className="fa fa-plus-circle me-2" aria-hidden="true"></i>Add State
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="card invoices-tabs-card">
                        <div className="invoices-main-tabs">
                            <div className="row align-items-center">
                                <div className="col-lg-12">
                                    <div className="invoices-tabs">
                                        <ul>
                                            <li><a href="/country">Country</a></li>
                                            <li><a href="/state" className="active">State</a></li>
                                            <li><a href="/city">City</a></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select className="form-select form-select-sm" style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input type="text" className="form-control form-control-sm d-inline-block"
                                                style={{ width: '200px' }} placeholder="Search states..."
                                                value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>State Name{getSortIcon('name')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('country')}>Country{getSortIcon('country')}</th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr><td colSpan="4" className="text-center"><div className="spinner-border" role="status"></div></td></tr>
                                                ) : currentItems.length > 0 ? (
                                                    currentItems.map((state, index) => (
                                                        <tr key={state.id}>
                                                            <td>{startIndex + index + 1}</td>
                                                            <td>{state.name || 'N/A'}</td>
                                                            <td>{state.country?.name || 'N/A'}</td>
                                                            <td className="d-flex align-items-center">
                                                                <a className="btn-action-icon bg-success-light me-2" style={{ cursor: 'pointer' }}
                                                                    data-bs-toggle="modal" data-bs-target="#edit_state"
                                                                    onClick={() => handleEditClick(state)} title="Edit">
                                                                    <i className="fa fa-edit"></i>
                                                                </a>
                                                                <a className="btn-action-icon bg-danger-light" style={{ cursor: 'pointer' }}
                                                                    onClick={() => handleDelete(state)} title="Delete">
                                                                    <i className="fa fa-trash-alt"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="4" className="text-center">No states found</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
                <Footer />
            </div>

            {/* Add State Modal */}
            <div className="modal custom-modal fade" id="add_state" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0"><i className="fa fa-map me-2 text-primary"></i>Add State</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form ref={formRef} onSubmit={handleSubmit} noValidate>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Country <span className="text-danger">*</span></label>
                                            <select name="country_id" value={newRecord.country_id} onChange={handleChange}
                                                className="form-control" required>
                                                <option value="">Select Country</option>
                                                {countries.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                            <div className="invalid-feedback">Please select a country.</div>
                                        </div>
                                    </div>
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>State Name <span className="text-danger">*</span></label>
                                            <input type="text" name="name" value={newRecord.name} onChange={handleChange}
                                                className="form-control" placeholder="e.g. Tamil Nadu" required />
                                            <div className="invalid-feedback">State name is required.</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2">Cancel</button>
                                <button type="submit" className="btn btn-primary paid-continue-btn">Add State</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Edit State Modal */}
            <div className="modal custom-modal fade" id="edit_state" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0"><i className="fa fa-edit me-2 text-primary"></i>Edit State</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>Country <span className="text-danger">*</span></label>
                                        <select name="country_id" value={newRecord.country_id} onChange={handleChange}
                                            className="form-control">
                                            <option value="">Select Country</option>
                                            {countries.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>State Name <span className="text-danger">*</span></label>
                                        <input type="text" name="name" value={newRecord.name} onChange={handleChange}
                                            className="form-control" placeholder="e.g. Tamil Nadu" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2">Cancel</button>
                            <button type="button" onClick={handleUpdate} className="btn btn-primary paid-continue-btn">Update State</button>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
}

export default State;